<template>
  <ion-app>
    <ion-router-outlet animated="false" />
  </ion-app>
</template>

<script>
import { IonApp, IonRouterOutlet, useBackButton, isPlatform } from "@ionic/vue";
import { defineComponent } from "vue";
import { App as CapApp } from "@capacitor/app";
import { useAuth0 } from "@auth0/auth0-vue";
import { Browser } from "@capacitor/browser";
import { redirectDeepLinkPath } from "@/utils/redirect-uri";
import { useRouter } from "vue-router";

// import { useStore } from 'vuex';

export default defineComponent({
  name: "App",
  components: {
    IonApp,
    IonRouterOutlet,
  },

  setup() {
    useBackButton(10, () => {
      // leave empty to not trigger any back button event
    });

    const { handleRedirectCallback, getAccessTokenSilently, isAuthenticated } =
      useAuth0();
    const router = useRouter();
    const isNative = isPlatform("android") || isPlatform("ios");
    const isNativeBrowser = isPlatform("mobileweb");
    // const store = useStore()

    CapApp.addListener("appUrlOpen", async ({ url }) => {
      console.log("🔵 appUrlOpen triggered with URL:", url);

      // Handle deep links first
      const needRedirect = redirectDeepLinkPath(router, url);
      if (needRedirect) {
        return;
      }

      let appState = null;
      let callbackHandled = false;

      if (
        url.includes("state") &&
        (url.includes("code") || url.includes("error"))
      ) {
        try {
          console.log("🔵 Handling redirect callback...");
          // Pass the URL directly to handleRedirectCallback - Auth0 SDK handles custom schemes
          const redirect = await handleRedirectCallback(url);
          appState = redirect.appState;
          callbackHandled = true;
          console.log("🔵 Callback handled successfully, appState:", appState);

          // Set a flag to prevent browserFinished from interfering
          sessionStorage.setItem("callbackHandled", "true");
        } catch (e) {
          console.error("🔵 Error in handleRedirectCallback:", e);
          // Handle access denied errors
          if (e.error === "access_denied") {
            if (e.error_description === "access denied") {
              router.push("/access-denied");
              return;
            }
          } else if (
            e.message === "Load failed" ||
            e.message?.includes("Load failed")
          ) {
            // Handle "Load failed" error - this happens when Auth0 SDK tries to fetch custom URL scheme
            console.log(
              "🔵 Load failed error caught - Auth0 SDK cannot fetch custom URL scheme"
            );
            console.log(
              "🔵 Checking if user is authenticated despite the error..."
            );

            // Wait a bit for Auth0 to process the callback server-side
            await new Promise((resolve) => setTimeout(resolve, 500));

            // Check if user is authenticated - if so, proceed with navigation
            if (isAuthenticated.value) {
              console.log(
                "🔵 User is authenticated, proceeding despite Load failed error"
              );
              callbackHandled = true;
              sessionStorage.setItem("callbackHandled", "true");
              // Set appState to null so navigation can proceed
              appState = null;
            } else {
              console.error(
                "🔵 User not authenticated after Load failed error"
              );
              // Don't re-throw - let navigation proceed anyway
            }
          } else {
            // Log other errors but don't re-throw to prevent unhandled rejection
            console.error("🔵 Error in handleRedirectCallback:", e);
            // Don't re-throw - let navigation proceed
          }
        }
      }

      // Close browser after handling callback (matching working implementation)
      try {
        console.log("🔵 Closing browser...");
        await Browser.close();
        console.log("🔵 Browser closed");
        // Small delay to ensure browser closes completely before navigation
        await new Promise((resolve) => setTimeout(resolve, 300));
      } catch (error) {
        console.log("🔵 Error closing browser:", error);
      }

      if (url.includes("/callback")) {
        console.log("🔵 Callback URL detected, navigating...");
        //init push notifications here
        if (isNative && !isNativeBrowser) {
          if (appState && appState.fullPathObj) {
            console.log(
              "🔵 Navigating to appState path:",
              appState.fullPathObj.path
            );
            router.push({
              path: appState.fullPathObj.path,
              query: appState.fullPathObj.query,
              hash: appState.fullPathObj.hash,
            });
          } else {
            // Fallback: navigate to home if no specific path
            console.log("🔵 No appState.fullPathObj, navigating to /home");
            router.push("/home");
          }
        }
      } else if (url.includes("/logout")) {
        console.log("🔵 Logout detected");
        // Use window.location.href to do a full page reload and clear state
        // After reload, the login page will auto-trigger login in in-app browser
        window.location.href = "/";
      }
    });
    console.log("Capapp: " + isAuthenticated.value);
    // TODO hacky way to know whether the app is login before
    sessionStorage.setItem("loginBefore", isAuthenticated.value);
  },
});
</script>
